// ---------------------------------------------------------------------------
// Cloud AI runner for the hosted web app: every model call goes to the backend
// (/api/ai/complete), which fronts Azure AI Foundry, meters the user's monthly
// credits, and records the usage ledger. The browser never holds a model key.
// ---------------------------------------------------------------------------

import { apiUrl } from '../config/cloud';
import { getAccessToken } from '../auth/auth';
import { reportExhausted, setCredits } from '../billing/credits';

/** Thrown when the monthly AI-credit allowance is used up (HTTP 402). */
export class AiCreditsError extends Error {
  readonly code = 'credits_exhausted';
  constructor(
    message: string,
    readonly used: number,
    readonly allowance: number,
    readonly tier: string,
  ) {
    super(message);
  }
}

interface CompleteResponse {
  text: string;
  credits?: { used: number; allowance: number };
}

/** Run one prompt through the backend AI endpoint. */
export async function cloudComplete(agent: string, prompt: string): Promise<string> {
  const token = await getAccessToken();
  if (!token) throw new Error('Please sign in to use AI features.');

  let res: Response;
  try {
    res = await fetch(apiUrl('/api/ai/complete'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent, prompt }),
    });
  } catch {
    throw new Error('Could not reach the AI service — check your connection and try again.');
  }

  if (res.status === 402) {
    const body = (await res.json().catch(() => null)) as
      | { error?: string; used?: number; allowance?: number; tier?: string }
      | null;
    reportExhausted({ used: body?.used ?? 0, allowance: body?.allowance ?? 0, tier: body?.tier ?? null });
    throw new AiCreditsError(
      body?.error ?? "You've used all your AI credits for this month.",
      body?.used ?? 0,
      body?.allowance ?? 0,
      body?.tier ?? 'free',
    );
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `AI request failed (${res.status}).`);
  }

  const body = (await res.json()) as CompleteResponse;
  if (body.credits) {
    setCredits({
      used: body.credits.used,
      allowance: body.credits.allowance,
      exhausted: body.credits.used >= body.credits.allowance,
    });
  }
  return body.text;
}

export interface AiUsage {
  tier: string;
  used: number;
  allowance: number;
  remaining: number;
}

/** Current-month usage for the signed-in account (also refreshes the credits store). */
export async function getAiUsage(): Promise<AiUsage> {
  const token = await getAccessToken();
  if (!token) throw new Error('Please sign in first.');
  const res = await fetch(apiUrl('/api/ai/usage'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Could not load AI usage (${res.status}).`);
  const usage = (await res.json()) as AiUsage;
  setCredits({ used: usage.used, allowance: usage.allowance, exhausted: usage.remaining <= 0, tier: usage.tier });
  return usage;
}
